import { useEffect, useRef, useState } from 'react';

export type StreamStatus = 'connecting' | 'open' | 'reconnecting' | 'failed';

/**
 * Flux SSE (EventSource, cookies de même origine). Reconnexion automatique du navigateur ;
 * l'état passe à « failed » si la connexion n'est pas rétablie après `failAfterMs`.
 */
export function useEventStream<T>(
  url: string | null,
  parse: (data: unknown) => T,
  onEvent: (event: T) => void,
  failAfterMs = 30_000,
): { status: StreamStatus; retry: () => void } {
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const [attempt, setAttempt] = useState(0);
  const handlers = useRef({ parse, onEvent });
  useEffect(() => {
    handlers.current = { parse, onEvent };
  });

  useEffect(() => {
    if (!url) return;
    const source = new EventSource(url);
    let failTimer: number | undefined;
    source.onopen = () => {
      setStatus('open');
      window.clearTimeout(failTimer);
      failTimer = undefined;
    };
    source.onmessage = (message: MessageEvent<string>) => {
      let event: T;
      try {
        event = handlers.current.parse(JSON.parse(message.data));
      } catch {
        return; // Message non conforme au schéma : ignoré.
      }
      handlers.current.onEvent(event);
    };
    source.onerror = () => {
      if (source.readyState === EventSource.CLOSED) {
        setStatus('failed');
        return;
      }
      setStatus('reconnecting');
      failTimer ??= window.setTimeout(() => {
        setStatus('failed');
        source.close();
      }, failAfterMs);
    };
    return () => {
      window.clearTimeout(failTimer);
      source.close();
    };
  }, [url, attempt, failAfterMs]);

  return {
    status,
    retry: () => {
      setStatus('connecting');
      setAttempt((value) => value + 1);
    },
  };
}
